import { apiFetch, qs } from '../lib/api';
import type { MatchSource } from './vehicles';

export interface WheelBoltPattern {
  id: number;
  bolt_pattern_raw: string;
  bolt_pattern_normalized: string;
  bolt_pattern_canonical: string;
  match_source: MatchSource | null;
  created_at: string;
}

export interface Wheel {
  id: number;
  shopify_product_id: number;
  shopify_variant_id: number;
  sku: string;
  brand: string;
  model: string;
  finish: string | null;
  diameter_in: number;
  width_in: number;
  offset_mm: number;
  backspacing_in: number | null;
  hub_bore_mm: number;
  load_rating_lbs: number | null;
  is_dually: boolean;
  is_beadlock: boolean;
  construction: string | null;
  position_hint: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  bolt_patterns_canonical?: string[];
  bolt_patterns?: WheelBoltPattern[];
  source_guide?: string | null;
  bolt_pattern_raw?: string | null;
  mfg_part_number?: string | null;
  vendor_part_number?: string | null;
  atd_part_number?: string | null;
  upc?: string | null;
  image_thumbnail?: string | null;
  image_small?: string | null;
  image_large?: string | null;
  dual_drilled_flag?: boolean;

  // Pricing (migration 006). Source fields are vendor-supplied and never
  // overwritten. effective_price_cents + price_source are derived.
  cost_cents?: number | null;
  map_price_cents?: number | null;
  msrp_cents?: number | null;
  retail_cents?: number | null;
  effective_price_cents?: number | null;
  price_source?: 'map' | 'retail' | 'cost_markup' | 'manual' | null;

  quantity_on_hand?: number | null;

  // Freshness timestamps (ISO strings)
  last_vendor_feed_at?: string | null;
  last_imported_at?: string | null;
  last_shopify_push_at?: string | null;
  last_pricing_sync_at?: string | null;
  last_inventory_sync_at?: string | null;

  // ATD per-warehouse buckets (CONUS-filtered)
  qty_local?: number | null;       // same-day pickup / install
  qty_local_plus?: number | null;  // next-day
  qty_nationwide?: number | null;  // 3-5 business days
  // Derived customer-facing install speed + vendor returnable flag
  install_speed?: 'same_day' | 'next_day' | 'standard' | 'estimated' | 'out_of_stock' | null;
  is_returnable?: boolean | null;
  inv_order_type?: string | null;
  // Per-warehouse ETA (mig 034). Vision pull writes these from the
  // priority chain (AL > NC > IN/TX > CA/WA); other vendors leave NULL.
  fastest_available_warehouse?: string | null;
  estimated_arrival_days?: '1-2' | '3' | '5' | null;
  inventory_source?: 'vision_per_warehouse' | 'atd_rollup' | 'wheelpros_inventory_json' | 'wheelpros_inventory_csv' | 'manual' | 'unknown' | null;

  // Derived at read time on the API. Captures whether the row's qty
  // numbers are trustworthy (fresh_in_stock), explicitly empty
  // (fresh_oos), never-known (unknown), or stale-WheelPros (stale,
  // last_inventory_sync_at older than 24h or null on a WP row).
  // effective_install_speed clamps stale/unknown/oos to 'out_of_stock'
  // so filters and sorts on install_speed can use it directly without
  // ranking unreliable rows as available.
  inventory_status?: 'fresh_in_stock' | 'fresh_oos' | 'unknown' | 'stale' | null;
  effective_install_speed?: 'same_day' | 'next_day' | 'standard' | 'estimated' | 'out_of_stock' | null;
  inventory_age_hours?: number | null;
  // Per-warehouse Vision qty fields (already on the wheel_products
  // schema since the Vision rollout). Surfaced to admin so the detail
  // page can show which DC carries stock.
  qty_al?: number | null;
  qty_nc?: number | null;
  qty_in?: number | null;
  qty_tx?: number | null;
  qty_ca?: number | null;
  qty_wa?: number | null;
  qty_nj?: number | null;

  // Wheel Pros numbered gallery angles (migration 015). image_url_1 is the
  // first additional angle; image_url_4 is last. ATD doesn't publish these.
  image_url_1?: string | null;
  image_url_2?: string | null;
  image_url_3?: string | null;
  image_url_4?: string | null;
  // Brand/logo image — separate from product media. Rendered alongside the
  // style code on the storefront wheel-detail page (not inside Shopify
  // product media).
  brand_logo_url?: string | null;
  // Image-verification gate — Shopify push blocks unless TRUE. Any PATCH
  // that touches an image field resets this to FALSE automatically.
  images_verified?: boolean;
  images_verified_at?: string | null;
  images_verified_by?: string | null;

  // Listing / SEO / metafields persisted to wheel_products by the listing
  // backfill + AI enrichment pipeline. Read-only on the detail page —
  // edits go through the listing regenerator, not direct PATCH.
  shopify_title?: string | null;
  shopify_handle?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  product_description?: string | null;
  description_source?: string | null;
  product_tags?: string[] | null;
  collection_tags?: string[] | null;
  metafields_json?: Record<string, unknown> | null;
  shopify_ready_status?: 'ready' | 'warning' | 'blocked' | 'excluded' | null;
  shopify_ready_reasons?: { blocking_reasons?: string[]; warnings?: string[] } | null;

  // Google Merchant / compliance fields
  country_of_origin?: string | null;
  hs_code?: string | null;
  google_product_category?: string | null;
  product_type?: string | null;
}

export interface WheelVariantSibling {
  id: number;
  sku: string;
  brand: string | null;
  model: string | null;
  finish: string | null;
  diameter_in: number | null;
  width_in: number | null;
  offset_mm: number | null;
  backspacing_in: number | null;
  hub_bore_mm: number | null;
  effective_price_cents: number | null;
  map_price_cents: number | null;
  msrp_cents: number | null;
  quantity_on_hand: number | null;
  image_thumbnail: string | null;
  image_small: string | null;
  image_large: string | null;
  shopify_ready_status: string | null;
  shopify_product_id: number | null;
  is_active: boolean;
  inventory_status: string | null;
  effective_install_speed: string | null;
  bolt_patterns_canonical: string[];
}

export type WheelInstallSpeed = 'same_day' | 'next_day' | 'standard' | 'estimated' | 'out_of_stock';
export type WheelSource = 'ATD' | 'WheelPros' | 'Vision' | 'Manual' | 'Other';
export type WheelSortBy =
  | 'sku' | 'brand' | 'model' | 'size' | 'install' | 'price' | 'qty' | 'updated';

export interface WheelListParams {
  // Multi-select filters — arrays serialize as repeated ?brand=foo&brand=bar.
  brand?: string[];
  source_guide?: WheelSource[];
  install_speed?: WheelInstallSpeed[];
  // Range
  diameter_min?: number;
  diameter_max?: number;
  // Single-value
  bolt_pattern?: string;
  q?: string;
  needs_review?: boolean;
  include_inactive?: boolean;
  // Sort
  sort_by?: WheelSortBy;
  sort_dir?: 'asc' | 'desc';
  // Pagination
  limit?: number;
  offset?: number;
}

export interface WheelListResponse {
  data: Wheel[];
  meta: { total: number; limit: number; offset: number };
}

export function listWheels(params: WheelListParams = {}): Promise<WheelListResponse> {
  return apiFetch(`/admin/wheels${qs(params as Record<string, unknown>)}`);
}

export interface WheelStats {
  total: number;
  by_source: Array<{ source_guide: string; count: number }>;
  brands: Array<{ brand: string; count: number }>;
  by_install_speed: {
    same_day: number;
    next_day: number;
    standard: number;
    estimated: number;
    out_of_stock: number;
  };
  needs_review: number;
  // Go-Live tiles — added 2026-04-29 for the launch dashboard.
  go_live_ready: number;
  already_pushed: number;
  pending_push: number;
  // BMF-group-level counts (= Shopify product counts). Added 2026-05-01
  // because the row-level counts above were inflated 10-50× since each
  // Shopify product covers many wheel_products rows. UI prefers these.
  ready_groups?: number;
  already_pushed_groups?: number;
  pending_push_groups?: number;
  blocked_groups?: number;
  image_missing_groups?: number;
}

export function getWheelStats(): Promise<WheelStats> {
  return apiFetch('/admin/wheels/stats');
}

// Push selected wheel rows to Shopify as DRAFT. Backend launches a one-shot
// Fargate task running shopify-push.js with --only-create-missing so existing
// Shopify products are never overwritten. Returns the ECS task ARN/id so the
// caller can link to CloudWatch.
export function pushSelectedWheels(ids: number[]): Promise<{
  task_arn: string;
  task_id: string;
  id_count: number;
  command: string;
}> {
  return apiFetch('/admin/wheels/push-selected', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

export function getWheel(id: number): Promise<Wheel> {
  return apiFetch(`/admin/wheels/${id}`);
}

export function getWheelVariants(id: number): Promise<{ variants: WheelVariantSibling[] }> {
  return apiFetch(`/admin/wheels/${id}/variants`);
}

export interface WheelPayload {
  shopify_product_id?: number;
  shopify_variant_id?: number;
  sku?: string;
  brand?: string;
  model?: string;
  finish?: string | null;
  diameter_in?: number;
  width_in?: number;
  offset_mm?: number;
  backspacing_in?: number | null;
  hub_bore_mm?: number;
  load_rating_lbs?: number | null;
  is_dually?: boolean;
  is_beadlock?: boolean;
  construction?: string | null;
  position_hint?: string | null;
  bolt_pattern_raw?: string;
  image_large?: string | null;
  image_small?: string | null;
  image_thumbnail?: string | null;
  image_url_1?: string | null;
  image_url_2?: string | null;
  image_url_3?: string | null;
  image_url_4?: string | null;
  brand_logo_url?: string | null;
}

export function verifyWheelImages(id: number, verified: boolean): Promise<Wheel> {
  return apiFetch(`/admin/wheels/${id}/verify-images`, {
    method: 'POST',
    body: JSON.stringify({ verified }),
  });
}

export function createWheel(payload: Required<Pick<WheelPayload,
  'shopify_product_id' | 'shopify_variant_id' | 'sku' | 'brand' | 'model' |
  'diameter_in' | 'width_in' | 'offset_mm' | 'hub_bore_mm' | 'bolt_pattern_raw'
>> & WheelPayload): Promise<Wheel> {
  return apiFetch('/admin/wheels', { method: 'POST', body: JSON.stringify(payload) });
}

export function updateWheel(id: number, patch: WheelPayload): Promise<Wheel> {
  return apiFetch(`/admin/wheels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteWheel(id: number): Promise<void> {
  return apiFetch(`/admin/wheels/${id}`, { method: 'DELETE' });
}

// Wipe every wheel_products row + cascade (bolt_patterns + fitment_matches).
// Requires ?confirm=YES on the server; the admin-UI button prompts twice
// before calling this.
export function deleteAllWheels(): Promise<{
  ok: true; deleted: { wheels: number; bolt_patterns: number; fitment_matches: number };
}> {
  return apiFetch('/admin/wheels/_all?confirm=YES', { method: 'DELETE' });
}
